import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Info, Plus, Trash2, KeyIcon } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { WebhookActionConfig } from "../../types/AgentAction";

interface AgentActionWebhookEditProps {
  config: WebhookActionConfig;
  onChange: (config: WebhookActionConfig) => void;
  actionName?: string;
  description?: string;
  onActionNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  showSaveActions?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isValid?: boolean;
}

const AgentActionWebhookEdit: React.FC<AgentActionWebhookEditProps> = ({
  config,
  onChange,
  actionName,
  description,
  onActionNameChange,
  onDescriptionChange,
  showSaveActions,
  onSave,
  onCancel,
  isSaving,
  isValid,
}) => {
  const [formData, setFormData] = useState<WebhookActionConfig>({
    timeout_ms: 600000, // 10 minutes default
    headers: {},
    auth: {
      type: "none",
    },
    ...config,
  });

  const [newHeaderKey, setNewHeaderKey] = useState("");
  const [newHeaderValue, setNewHeaderValue] = useState("");

  useEffect(() => {
    const newFormData = {
      timeout_ms: 600000,
      headers: {},
      auth: {
        type: "none",
      },
      ...config,
    };
    setFormData(newFormData);
  }, [config]);

  const updateConfig = (updates: Partial<WebhookActionConfig>) => {
    const newConfig = { ...formData, ...updates };
    setFormData(newConfig);
    onChange(newConfig);
  };

  const handleAuthChange = (field: string, value: string) => {
    updateConfig({
      auth: {
        ...formData.auth,
        [field]: value,
      },
    });
  };

  const addHeader = () => {
    if (newHeaderKey && newHeaderValue) {
      updateConfig({
        headers: {
          ...formData.headers,
          [newHeaderKey]: newHeaderValue,
        },
      });
      setNewHeaderKey("");
      setNewHeaderValue("");
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    updateConfig({ headers: newHeaders });
  };

  return (
    <div className="space-y-6">
      {/* Action Name and Description */}
      {onActionNameChange && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="action-name"
              className="text-sm font-gc-med text-foreground">
              Action Name
            </Label>
            <Input
              id="action-name"
              value={actionName || ""}
              onChange={(e) => onActionNameChange(e.target.value)}
              placeholder="e.g., Wait for Payment Confirmation"
            />
          </div>

          {onDescriptionChange && (
            <div className="space-y-2">
              <Label
                htmlFor="action-description"
                className="text-sm font-gc-med text-foreground">
                Description
              </Label>
              <Textarea
                id="action-description"
                value={description || ""}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Describe what this webhook action does"
                rows={2}
              />
            </div>
          )}
        </div>
      )}

      {/* Headers */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-gc-med text-foreground">
            Headers (Optional)
          </h3>
          <Tooltip content="Additional HTTP headers to include in the webhook registration request">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-2">
          {Object.entries(formData.headers || {}).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <Input value={key} disabled className="flex-1 bg-muted" />
              <Input value={value} disabled className="flex-1 bg-muted" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeHeader(key)}
                className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <Input
              placeholder="Header name"
              value={newHeaderKey}
              onChange={(e) => setNewHeaderKey(e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="Header value"
              value={newHeaderValue}
              onChange={(e) => setNewHeaderValue(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  addHeader();
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={addHeader}
              disabled={!newHeaderKey || !newHeaderValue}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <KeyIcon className="w-5 h-5 text-maitai-green" />
          <h3 className="text-sm font-gc-med text-foreground">
            Authentication (Optional)
          </h3>
          <Tooltip content="Configure authentication for the webhook registration request">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="auth-type"
              className="text-sm font-gc-med text-foreground">
              Authentication Type
            </Label>
            <Select
              value={formData.auth?.type || "none"}
              onValueChange={(value) => handleAuthChange("type", value)}>
              <SelectTrigger id="auth-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="bearer">Bearer Token</SelectItem>
                <SelectItem value="api_key">API Key</SelectItem>
                <SelectItem value="basic">Basic Auth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.auth?.type === "bearer" && (
            <div className="space-y-2">
              <Label
                htmlFor="bearer-token"
                className="text-sm font-gc-med text-foreground">
                Bearer Token
              </Label>
              <Input
                id="bearer-token"
                type="password"
                value={formData.auth?.token || ""}
                onChange={(e) => handleAuthChange("token", e.target.value)}
                placeholder="Enter bearer token"
                className="font-mono text-sm"
              />
            </div>
          )}

          {formData.auth?.type === "api_key" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="api-key-header"
                  className="text-sm font-gc-med text-foreground">
                  Header Name
                </Label>
                <Input
                  id="api-key-header"
                  value={formData.auth?.header || ""}
                  onChange={(e) => handleAuthChange("header", e.target.value)}
                  placeholder="X-API-Key"
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="api-key-value"
                  className="text-sm font-gc-med text-foreground">
                  API Key
                </Label>
                <Input
                  id="api-key-value"
                  type="password"
                  value={formData.auth?.key || ""}
                  onChange={(e) => handleAuthChange("key", e.target.value)}
                  placeholder="Enter API key"
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}

          {formData.auth?.type === "basic" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="basic-username"
                  className="text-sm font-gc-med text-foreground">
                  Username
                </Label>
                <Input
                  id="basic-username"
                  value={formData.auth?.username || ""}
                  onChange={(e) => handleAuthChange("username", e.target.value)}
                  placeholder="Enter username"
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="basic-password"
                  className="text-sm font-gc-med text-foreground">
                  Password
                </Label>
                <Input
                  id="basic-password"
                  type="password"
                  value={formData.auth?.password || ""}
                  onChange={(e) => handleAuthChange("password", e.target.value)}
                  placeholder="Enter password"
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeout */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-gc-med text-foreground">Timeout</h3>
          <Tooltip content="How long the agent will wait for the webhook callback (in milliseconds)">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-2">
          <Input
            type="number"
            value={formData.timeout_ms || 600000}
            onChange={(e) =>
              updateConfig({ timeout_ms: parseInt(e.target.value) || 600000 })
            }
            min={1000}
            max={3600000}
          />
          <p className="text-xs text-muted-foreground">
            Timeout in milliseconds (1000ms = 1 second). Default: 600000ms (10
            minutes)
          </p>
        </div>
      </div>

      {/* Save Actions */}
      {showSaveActions && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || !isValid}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgentActionWebhookEdit;
